<template>
  <div>
    <b-form @submit.prevent="onSubmit">
      <b-row>
        <b-col md="6" v-if="methodType == 'CALENDAR'">
          <b-form-group label="Due (Date)" label-for="due-date">
            <b-form-input
              id="due-date"
              class="w-100"
              type="date"
              v-model="dueDateLocal"
              :disabled="true"
              required
            />
          </b-form-group>
        </b-col>

        <b-col md="6" v-if="methodType == 'COUNTER'">
          <label for="possible_maintenance_hours">Due Running Hours</label>
          <InputCurrency
            prepend=""
            append="Hours"
            id="possible_maintenance_hours"
            v-model.number="form.process.possible_maintenance_hours"
            type="number"
            min="0"
            placeholder="Masukkan RH"
            disabled
          />
        </b-col>

        <b-col md="6">
          <b-form-group label="Maintenance Date *" label-for="maintenance-date">
            <b-form-input
              class="w-100"
              id="maintenance-date"
              type="date"
              v-model="form.process.maintenance_date"
              required
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Cost">
            <b-input-group>
              <b-input-group-prepend>
                <b-input-group-text>Rp</b-input-group-text>
              </b-input-group-prepend>
              <b-form-input
                v-model="costInput"
                @input="onCostInput"
                placeholder="0"
              />
            </b-input-group>
          </b-form-group>
        </b-col>

        <template v-if="methodType == 'COUNTER'">
          <b-col md="3">
            <label for="maintenance-running-hours">
              <i
                v-if="form.process.maintenance_date"
                id="actual-rh-warning"
                class="fas fa-exclamation-circle text-danger mr-1"
                style="font-size: 14px"
              ></i>
              <b-tooltip
                v-if="form.process.maintenance_date"
                target="actual-rh-warning"
                placement="top"
              >
                The data is actual, reference from feature
                <span
                  class="text-warning cursor-pointer"
                  style="text-decoration: underline"
                  @click="goToRunningHours"
                >
                  Running Hours
                </span>
              </b-tooltip>
              Actual Rh
            </label>
            <InputCurrency
              prepend=""
              id="maintenance-running-hours"
              append="Hours"
              v-model.number="form.process.actual_running_hours"
              type="number"
              min="0"
              placeholder="0.00"
              disabled
            />
          </b-col>
          <b-col md="3" class="pl-0">
            <label for="maintenance-running-hours">
              Input RH
              <span class="text-danger">*</span>
            </label>
            <InputCurrency
              prepend=""
              id="maintenance-running-hours"
              append="Hours"
              v-model.number="form.process.maintenance_running_hours"
              type="number"
              min="0"
              placeholder="0.00"
            />
          </b-col>
        </template>
      </b-row>

      <!-- Attachments -->
      <b-form-group label="Maintenance File">
        <b-form-file
          multiple
          :state="true"
          @change="onFilesSelected"
          browse-text="Browse"
          placeholder="Pilih file..."
          drop-placeholder="Drop files here..."
          :key="files.length"
        />
        <div class="mt-2">
          <b-badge
            v-for="(f, idx) in files"
            :key="`f-${idx}`"
            variant="warning"
            pill
            class="mr-2 align-middle mb-2"
          >
            {{ f.name }}
            <b-button
              size="xs"
              variant="link"
              class="p-0 ml-1"
              @click="removeFile(idx)"
            >
              ×
            </b-button>
          </b-badge>
        </div>
        <small class="text-muted d-block mt-1">
          File Type: .pdf .xls .xlsx .doc .docx .jpg .jpeg .png — Max File Size:
          2 MB
        </small>
      </b-form-group>

      <!-- Notes -->
      <b-form-group label="Notes">
        <b-form-textarea
          plain
          v-model="form.process.notes"
          rows="5"
          placeholder="Tulis catatan..."
        />
      </b-form-group>

      <small class="text-muted">
        Sign * :
        <strong class="text-black">Mandatory (Must Fill)</strong>
      </small>
      <div class="w-100 d-flex align-items-center justify-content-between mt-4">
        <div class="ml-auto d-flex w-100">
          <b-button
            variant="outline-secondary"
            class="mr-2 w-50"
            :disabled="loading"
            @click="$emit('close')"
          >
            Cancel & Close
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="loading"
            class="w-50"
          >
            <b-spinner small class="mr-2" v-if="loading" />
            Submit
          </b-button>
        </div>
      </div>
    </b-form>
  </div>
</template>

<script>
import moment from '@/node_modules/moment/moment'
import InputCurrency from '@/src/components/global/InputCurrency.vue'
import { pmsActions } from '@/src/Utils/helper'

export default {
  name: 'FormMaintenanceProcessSingle',
  components: {
    InputCurrency,
  },
  props: {
    selectedData: {
      type: Object,
      default: () => ({}),
    },
    methodType: {
      type: String,
      default: 'CALENDAR', // or 'COUNTER'
    },
  },
  data() {
    return {
      dueDateLocal: this.selectedData?.possible_maintenance_date,
      loading: false,
      files: [],
      costInput: '',
      form: {
        action: 'process',
        process: {
          maintenance_date: null,
          possible_maintenance_hours: this.selectedData
            ? this.selectedData.possible_maintenance_hours
            : 0,
          maintenance_running_hours: null, // used when COUNTER
          cost: 0,
          notes: '',
          finish_notes: '',
          maintenance_attachments: [],
          actual_running_hours: null,
        },
      },
    }
  },
  watch: {
    'form.process.maintenance_date'(newDate) {
      // Only fetch actual running hours when method type is COUNTER and date is provided
      if (this.methodType === 'COUNTER' && newDate) {
        this.fetchActualRhByDate()
      }
    },
  },
  methods: {
    ...pmsActions,
    async fetchActualRhByDate() {
      try {
        const res = await this.getRhByDate({
          vehicle_power_id: this.selectedData.vehicle_power_id,
          power_series_id: this.selectedData.power_series_id,
          power_type_id: this.selectedData.power_type_id,
          date: this.form.process.maintenance_date,
        })
        if (res.status === 'success') {
          const { data } = res.data
          this.form.process.actual_running_hours = data.total_running_hours
          this.form.process.maintenance_running_hours = data.total_running_hours
        }
      } catch (error) {
        console.log('Error fetching actual running hours:', error)
      }
    },
    onFilesSelected(event) {
      const list = Array.from(event.target.files || [])
      // optional: filter by max size 2MB
      this.files = list.filter((f) => f.size <= 2 * 1024 * 1024)
    },
    removeFile(index) {
      this.files.splice(index, 1)
    },
    onCostInput() {
      const numeric = (this.costInput || '').toString().replace(/[^0-9]/g, '')
      const asNumber = numeric ? parseInt(numeric, 10) : 0
      this.form.process.cost = asNumber
      this.costInput = new Intl.NumberFormat('id-ID').format(asNumber)
    },
    goToRunningHours() {
      const query = {
        search: this.selectedData?.vehicle_name,
        date: this.form.process.maintenance_date,
      }
      this.$router.push({
        path: '/maintenance/running-hour-new',
        query: query,
      })
    },

    async handleUploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)
      const res = await this.uploadFilePms(formData)
      console.log('Upload file response:', res)
      return res.data?.data || null
    },

    async buildPayload() {
      const uploadedFiles = await Promise.all(
        this.files.map((f) => this.handleUploadFile(f))
      )

      // Filter hanya file yang berhasil (tidak null atau undefined)
      const validFiles = uploadedFiles.filter((f) => f)
      return {
        action: 'process',
        process: {
          maintenance_date: this.form.process.maintenance_date
            ? moment(this.form.process.maintenance_date).format('YYYY-MM-DD')
            : null,
          maintenance_running_hours:
            this.methodType === 'COUNTER'
              ? this.form.process.maintenance_running_hours
              : null,
          cost: this.form.process.cost,
          notes: this.form.process.notes,
          finish_notes: this.form.process.finish_notes,
          maintenance_attachments: validFiles,
        },
      }
    },
    async onSubmit() {
      this.loading = true
      try {
        const body = await this.buildPayload()
        this.$emit('submit', payload)
        const payload = {
          body,
          maintenanceId: this.selectedData.id,
        }
        const res = await this.actionMaintenance(payload)
        console.log('Maintenance process response:', res)

        if (res.status == 'success') {
          this.$swal.fire(
            'Success',
            res.data.message || 'Maintenance processed successfully.',
            'success'
          )
          this.$emit('refetch')
          this.$emit('close')
        } else {
          this.$swal.fire(
            'Error',
            res.data.message || 'Failed to process maintenance.',
            'error'
          )
        }
      } catch (e) {
        this.$swal.fire(
          'Error',
          e.response?.data?.message || 'An unexpected error occurred.',
          'error'
        )
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.badge .btn-link {
  color: inherit;
}
</style>
